import { Stack, Typography, Tooltip, Button, Box, useTheme, useMediaQuery } from "@mui/material"
import { DisplayBoxBase } from "../../../components/shared/StyledComponents"
import { MessageCirclePlus, CalendarPlus, Phone, Timer, CircleCheck } from "lucide-react"
import { MeetingDisplay } from "../../meeting/components/MeetingDisplay"
import { CalendlyScheduler } from "../../meeting"
import { getAdvisorMeetings } from "../../../core/services/gutcheck-api.service"
import { useEffect, useState, useMemo } from "react"
import { IScheduler, IMeeting, IAdvisor, IProject, ISurveyLink, SchedulerType, IViewpoint, TouchpointType } from "../../../types"
import ClientProvider from "../../../modules/ClientProvider"
import Loading from "../../../components/Loading"
import { LoginButton } from "../../../components/buttons/login-button"
import { useSchedulers } from "../../../hooks"

export const OnboardingSteps = ({ advisor, project, surveyLink }: { advisor: IAdvisor, project: IProject, surveyLink: ISurveyLink }) => {

    const [meetings, setMeetings] = useState<IMeeting[]>([]);
    const [isCalendlyOpen, setIsCalendlyOpen] = useState<boolean>(false);
    const [meetingCreated, setMeetingCreated] = useState<boolean>(false);
    const [activeScheduler, setActiveScheduler] = useState<IScheduler | null>(null);

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    // Memoize the types array to prevent infinite loops
    const schedulerTypes = useMemo(() => [
        SchedulerType.INTRO,
        SchedulerType.DEMO,
        SchedulerType.ONBOARDING
    ], []);

    // Use the new hook to fetch schedulers
    const { schedulers, loading: schedulersLoading } = useSchedulers({
        projectTag: project?.tag || "",
        types: schedulerTypes,
        isPublic: true,
        enabled: !!(advisor && project)
    });

    const introScheduler = schedulers[SchedulerType.INTRO];
    const demoScheduler = schedulers[SchedulerType.DEMO];
    const onboardingScheduler = schedulers[SchedulerType.ONBOARDING];

    useEffect(() => {
        if (advisor && project) {
            fetchMeetings();
        }
    }, [advisor?.public_id, project?.tag]);

    const fetchMeetings = async () => {
        if (advisor) {
            const { data, error } = await getAdvisorMeetings(advisor.public_id);
            if (data) {
                setMeetings(data.meetings);
            }
        }
    }

    const handleMeetingCreated = async () => {
        setMeetingCreated(true);
        setIsCalendlyOpen(false);

        setTimeout(async () => {
            fetchMeetings();
        }, 2000);
    }

    const handleBookIntro = async () => {
        if (!introScheduler) {
            console.warn("Intro scheduler not available");
            return;
        }
        setActiveScheduler(introScheduler);
        launchCalendly();
    }

    const handleBookDemo = async () => {
        if (!demoScheduler) {
            console.warn("Demo scheduler not available");
            return;
        }
        setActiveScheduler(demoScheduler);
        launchCalendly();
    }

    const handleBookOnboarding = async () => {
        if (!onboardingScheduler) {
            console.warn("Onboarding scheduler not available");
            return;
        }
        setActiveScheduler(onboardingScheduler);
        launchCalendly();
    }

    const handleSetOpen = async (state: boolean) => {
        if (state) {
            setIsCalendlyOpen(true);
        } else {
            setIsCalendlyOpen(false);
            setActiveScheduler(null);
        }
    }

    const launchCalendly = () => {
        setIsCalendlyOpen(true);
    }

    if (!advisor || !project) {
        return <Loading />;
    }

    const advisorLink = advisor.latest_survey_url || advisor.link;
    const name = advisor.user?.profile?.name || advisor.name;
    const email = advisor.user?.email_address || advisor.email_address;
    const dashboardLink = `https://${project.tag}.${process.env.REACT_APP_APP_ORIGIN}/dashboard?emh=${advisor.public_id}&theme=advisor`;

    const introMeeting = meetings?.find(meeting => meeting.scheduler?.type === "intro");
    const demoMeeting = meetings?.find(meeting => meeting.scheduler?.type === "demo");
    const onboardingMeeting = meetings?.find(meeting => meeting.scheduler?.type === "onboarding");
    const defaultViewpoint = advisor?.viewpoints?.find((viewpoint: IViewpoint) => viewpoint.touchpoint?.type === TouchpointType.DEFAULT);

    // Prepare box and action content for each step
    const steps = advisor.intro_enabled ? [
        {
            box: (
                <DisplayBoxBase className="active" sx={{
                    display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: "10px", flex: 1, "& span": {
                        fontSize: '1rem', fontWeight: 'bold', color: '#fff', borderRadius: '50%', backgroundColor: '#000', width: '25px', height: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px'
                    }
                }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><span>1</span>Explore the Fit</Typography>
                    <Typography variant="body1">15 minute video session to learn a bit about the product, share your initial insights, then decide if you'd like to advise.</Typography>
                </DisplayBoxBase>
            ),
            action: (
                advisor.status === "complete" ? (
                    <Button variant="text" color="primary" sx={{ width: '100%' }}><Timer size={14} />&nbsp;&nbsp;In Review</Button>
                ) : advisor.status === "approved" ? (
                    <Button variant="text" color="success" sx={{ width: '100%' }}><CircleCheck size={14} />&nbsp;&nbsp;Approved</Button>
                ) : advisor?.latest_survey_url ? (
                    <Button variant="contained" color="primary" href={advisorLink} sx={{ width: '100%' }}><MessageCirclePlus size={14} />&nbsp;&nbsp;Continue</Button>
                ) : defaultViewpoint && defaultViewpoint.alchemer_survey_url ? (
                    <Button variant="contained" color="primary" href={defaultViewpoint.alchemer_survey_url} sx={{ width: '100%' }}><MessageCirclePlus size={14} />&nbsp;&nbsp;Continue</Button>
                ) : (
                    <Button variant="contained" color="primary" href={advisorLink} sx={{ width: '100%' }}><MessageCirclePlus size={14} />&nbsp;&nbsp;Start Now</Button>
                )
            )
        },
        {
            box: (
                <DisplayBoxBase sx={{
                    display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: "10px", flex: 1, "& span": {
                        fontSize: '1rem', fontWeight: 'bold', color: '#fff', borderRadius: '50%', backgroundColor: '#000', width: '25px', height: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px'
                    }
                }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><span>2</span>{project.effective_scheduler_properties?.[SchedulerType.INTRO]?.name}</Typography>
                    <Typography variant="body1">{project.effective_scheduler_properties?.[SchedulerType.INTRO]?.description}</Typography>
                </DisplayBoxBase>
            ),
            action: (
                advisor.status !== 'approved' ? (
                    <Tooltip title="Provide feedback on the company first">
                        <span>
                            <Button variant="text" disabled={true} color="primary" sx={{ width: '100%' }}><CalendarPlus size={14} />&nbsp;&nbsp;Book an Intro</Button>
                        </span>
                    </Tooltip>
                ) : introMeeting ? (
                    <Box sx={{ width: 'fit-content', margin: '0 auto' }}>
                        <MeetingDisplay meeting={introMeeting} advisor={advisor} size="small" />
                    </Box>
                ) : introScheduler ? (
                    <Button variant="contained" color="primary" sx={{ width: '100%' }} onClick={handleBookIntro}><CalendarPlus size={14} />&nbsp;&nbsp;Book an Intro</Button>
                ) : (
                    <Button variant="text" disabled={true} color="primary" sx={{ width: '100%' }}><CalendarPlus size={14} />&nbsp;&nbsp;Not Available</Button>
                )
            )
        },
        {
            box: (
                <DisplayBoxBase sx={{
                    display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: "10px", flex: 1, "& span": {
                        fontSize: '1rem', fontWeight: 'bold', color: '#fff', borderRadius: '50%', backgroundColor: '#000', width: '25px', height: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px'
                    }
                }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><span>3</span>{project.effective_scheduler_properties?.[SchedulerType.DEMO]?.name}</Typography>
                    <Typography variant="body1">{project.effective_scheduler_properties?.[SchedulerType.DEMO]?.description}</Typography>
                </DisplayBoxBase>
            ),
            action: (
                demoMeeting ? (
                    <MeetingDisplay meeting={demoMeeting} advisor={advisor} size="small" />
                ) : introMeeting && demoScheduler ? (
                    <Button variant="contained" color="primary" sx={{ width: '100%' }} onClick={handleBookDemo}><CalendarPlus size={14} />&nbsp;&nbsp;Book a Demo</Button>
                ) : introMeeting && !demoScheduler ? (
                    <Button variant="text" disabled={true} color="primary" sx={{ width: '100%' }}><CalendarPlus size={14} />&nbsp;&nbsp;Not available</Button>
                ) : (
                    <Tooltip title="Complete the intro meeting first">
                        <span>
                            <Button variant="text" disabled={true} color="primary" sx={{ width: '100%' }}><CalendarPlus size={14} />&nbsp;&nbsp;Book a Demo</Button>
                        </span>
                    </Tooltip>
                )
            )
        }
    ] : advisor.status !== "approved" ? [
        {
            box: (
                <DisplayBoxBase className="active" sx={{
                    width: '50%', maxWidth: '50%', margin: '0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: "10px", flex: 1, "& span": {
                        fontSize: '1rem', fontWeight: 'bold', color: '#fff', borderRadius: '50%', backgroundColor: '#000', width: '25px', height: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px'
                    }
                }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>See if it's a good fit</Typography>
                    <Typography variant="body1">Take a 15 minute video survey to learn a bit about the product, share your initial insights, then decide if you'd like to advise.</Typography>
                </DisplayBoxBase>
            ),
            action: (
                <Box sx={{ width: '50%', maxWidth: '50%', margin: '0 auto' }}>
                    {advisor.status === "complete" ? (
                        <Button variant="outlined" color="primary" disabled={true} sx={{ width: '100%', maxWidth: '100%' }}><Timer size={14} />&nbsp;&nbsp;In Review</Button>
                    ) : advisor.status === "approved" ? (
                        <Button variant="outlined" color="success" disabled={true} sx={{ width: '100%', maxWidth: '100%' }}><CircleCheck size={14} />&nbsp;&nbsp;Approved</Button>
                    ) : advisor?.latest_survey_url ? (
                        <Button variant="contained" color="primary" href={advisorLink} sx={{ width: '100%', maxWidth: '100%' }}><MessageCirclePlus size={14} />&nbsp;&nbsp;Continue</Button>
                    ) : defaultViewpoint && defaultViewpoint.alchemer_survey_url ? (
                        <Button variant="contained" color="primary" href={defaultViewpoint.alchemer_survey_url} sx={{ width: '100%', maxWidth: '100%' }}><MessageCirclePlus size={14} />&nbsp;&nbsp;Continue</Button>
                    ) : (
                        <Button variant="contained" color="primary" href={advisorLink} sx={{ width: '100%', maxWidth: '100%' }}><MessageCirclePlus size={14} />&nbsp;&nbsp;Start Now</Button>
                    )}
                </Box>
            )
        }
    ] : [
        
    ];

    let extraSections = null;

    if (advisor.status === "approved" && (!advisor.intro_enabled || (introMeeting && (demoMeeting || !demoScheduler)))) {
        extraSections = (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '60px' }}>
                <Typography variant="body1" fontSize={"1.2rem"} fontWeight="bold" sx={{ textAlign: 'center' }}>
                    {advisor.intro_enabled ? "You're all set!" : "Ready to get started?"}
                </Typography>
                <Box>
                    <Button variant="contained" onClick={() => {
                        window.location.href = dashboardLink;
                    }}>
                        Log in to your dashboard
                    </Button>
                </Box>
            </Box>
        );
    }
    let onboardingSection = null;
    if (onboardingMeeting) {
        onboardingSection = (
            <Box display="flex" className="display-box" flexDirection={{ xs: "column", md: "row" }} sx={{ justifyContent: 'space-between', alignItems: 'center', gap: '20px' }} mt={2} mb={4}>
                <Box>
                    <Typography variant="body1" fontWeight="bold" fontSize={"1rem"}>Get ready for your call</Typography>
                    <Typography variant="body1" sx={{ fontSize: '0.9rem' }}>You have an upcoming call with an advisor specialist who can help determine if you're a good match for {project?.name}.</Typography>
                </Box>
                <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 460px" } }}>
                    <MeetingDisplay meeting={onboardingMeeting} advisor={advisor} size="normal" />
                </Box>
            </Box>
        );
    } else {
        onboardingSection = (
            <Box display="flex" className="display-box" flexDirection={{ xs: "column", md: "row" }} sx={{ justifyContent: 'space-between', alignItems: 'center', gap: '20px' }} mt={2} mb={4}>
                <Box>
                    <Typography variant="body1" fontWeight="bold" fontSize={"1rem"}>Not sure if you're a good fit?</Typography>
                    <Typography variant="body1" sx={{ fontSize: '0.9rem' }}>Book time with an advisor specialist who can help determine if you're a good match for {project?.name}.</Typography>
                </Box>
                <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 460px" } }}>
                    {onboardingScheduler ? (
                        <Button variant="outlined" color="primary" onClick={handleBookOnboarding} sx={{ width: '100%', whiteSpace: 'nowrap' }}><Phone size={14} />&nbsp;&nbsp;Schedule a Call</Button>
                    ) : (
                        <Button variant="outlined" disabled={true} color="primary" sx={{ width: '100%', whiteSpace: 'nowrap' }}><Phone size={14} />&nbsp;&nbsp;Not Available</Button>
                    )}
                </Box>
            </Box>
        );
    }

    if (isDesktop) {
        // Desktop: two rows, boxes then actions
        console.log(steps);
        return (
            <>
                {steps.length > 0 && (
                    <Stack spacing={2} mt={2} mb={6}>
                        <Stack direction="row" spacing={2} sx={{ width: '100%', alignItems: 'stretch' }}>
                            {steps.map((step, idx) => (
                                <Box key={idx} flex={1} display="flex">{step.box}</Box>
                            ))}
                        </Stack>
                        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                            {steps.map((step, idx) => (
                                <Box key={idx} flex={1}>{step.action}</Box>
                            ))}
                        </Stack>
                    </Stack>
                )}
                {extraSections}
                {onboardingSection}
                {isCalendlyOpen && activeScheduler && <CalendlyScheduler project={project} scheduler={activeScheduler} advisor={advisor} surveyLink={surveyLink} onMeetingCreated={handleMeetingCreated} name={name} email={email} inline={false} open={isCalendlyOpen} setOpen={handleSetOpen} />}
            </>
        );
    } else {
        // Mobile: stack each box with its action
        return (
            <>
                {steps.length > 0 && (
                    <Stack spacing={6} mt={2} mb={6} >
                        {steps.map((step, idx) => (
                            <Stack key={idx} spacing={2} alignItems="center">
                                {step.box}
                                {step.action}
                            </Stack>
                        ))}
                    </Stack>
                )}
                {extraSections}
                {onboardingSection}
                {isCalendlyOpen && activeScheduler && <CalendlyScheduler project={project} scheduler={activeScheduler} advisor={advisor} surveyLink={surveyLink} onMeetingCreated={handleMeetingCreated} name={name} email={email} inline={false} open={isCalendlyOpen} setOpen={handleSetOpen} />}
            </>
        );
    }
}